import { forwardRef, inject, Injectable, Provider } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { BehaviorSubject, Observable, Subscription, tap } from 'rxjs';
import { IAuthService, IAuthServiceToken } from './auth-service.interface';
import { ENVIRONMENT } from 'projects/shared/lib/environment/environment';
import { EPermission } from 'shared/services/INTEGRA_Shop_Client';
import { IInitializable, INITIALIZABLE_SERVICES } from 'shared/app-core/initializable.interface';
import { ILocalStorageService, ILocalStorageServiceToken } from '../localstorage/localstorage-service.interface';
import { IContextService, IContextServiceToken } from '../context/context-service.interface';

@Injectable({
  providedIn: null,
  deps: [
    ILocalStorageServiceToken
  ]
})
export class AuthService implements IAuthService, IInitializable {

  private readonly contextService?: IContextService | undefined | null = inject(IContextServiceToken, { optional: true });

  protected jwtHelper = new JwtHelperService();

  public accessJWTSubject: BehaviorSubject<string | undefined>;

  public userNameSubject: BehaviorSubject<string | undefined>;
  public userInitialsSubject: BehaviorSubject<string | undefined>;
  public userNumberSubject: BehaviorSubject<string | undefined>;
  public customerNumberSubject: BehaviorSubject<string | undefined>;
  public coUserNumberSubject: BehaviorSubject<string | undefined>;

  protected authenticatedSource: BehaviorSubject<boolean>;
  protected authenticatedChanged: Observable<boolean>;

  private cookieTermsAcceptedStateSource: BehaviorSubject<boolean>;
  private cookieTermsAcceptedStateChanged: Observable<boolean>;
  private accessJWTSubscription: Subscription | undefined;
  private userNumber: string | undefined;

  protected lStorage: ILocalStorageService = inject(ILocalStorageServiceToken);

  public accessJWTRefreshInProgress: boolean = false;
  public readonly accessJWTRefreshSubject: BehaviorSubject<string | undefined> = new BehaviorSubject<string | undefined>(undefined);

  private userPermissionSubject: BehaviorSubject<string[]>;

  public constructor() {
    // cookie
    let hasAcceptedCookieTerms: boolean = this.lStorage.cookieTermsAcceptanceStateGet();

    this.cookieTermsAcceptedStateSource = new BehaviorSubject<boolean>(hasAcceptedCookieTerms);
    this.cookieTermsAcceptedStateChanged = this.cookieTermsAcceptedStateSource.asObservable();

    // accessJWT
    let accessJWT = this.lStorage.accessJWTGet();
    let isAccessJWTExpired: boolean = true;

    let jwtDecoded: any;
    if (accessJWT) {
      isAccessJWTExpired = this.jwtHelper.isTokenExpired(accessJWT);
      jwtDecoded = this.jwtHelper.decodeToken(accessJWT);
    }
    this.userPermissionSubject = new BehaviorSubject<string[]>(this.jwtPermissionToStringListGet(jwtDecoded?.Permission));


    let hasLoginCookiesAccepted = ENVIRONMENT.auth.loginRequiresCookiesAcceptance ? hasAcceptedCookieTerms : true;
    let isAuthenticated = hasLoginCookiesAccepted && !isAccessJWTExpired;
    this.accessJWTSubject = new BehaviorSubject<string | undefined>(accessJWT);

    this.userNameSubject = new BehaviorSubject<string | undefined>(undefined);
    this.userInitialsSubject = new BehaviorSubject<string | undefined>(undefined);
    this.userNumberSubject = new BehaviorSubject<string | undefined>(undefined);
    this.customerNumberSubject = new BehaviorSubject<string | undefined>(undefined);
    this.coUserNumberSubject = new BehaviorSubject<string | undefined>(undefined);

    this.authenticatedSource = new BehaviorSubject<boolean>(isAuthenticated);
    this.authenticatedChanged = this.authenticatedSource.asObservable();
  }

  public init(): void | Promise<void> {
    this.accessJWTSubscriptionInit();
  }

  private accessJWTSubscriptionInit() {
    if (!this.accessJWTSubscription) {
      this.accessJWTSubscription = this.accessJWTSubject.pipe(
        tap((jwt: string | undefined) => {
          let jwtDecoded: any;
          if (jwt != undefined) {
            jwtDecoded = this.jwtHelper.decodeToken(jwt);
            this.userPermissionSubject.next(this.jwtPermissionToStringListGet(jwtDecoded?.Permission));
          } else {
            this.userPermissionSubject.next([]);
          }

          setTimeout(() => {
            let authenticationState: boolean = jwt ? true : false;
            this.authenticationStateUpdate(authenticationState);

            this.userNameUpdate(jwtDecoded?.UserName);
            this.userInitialsUpdate(jwtDecoded?.UserInitials);
            this.userNumberUpdate(jwtDecoded?.UserNumber);
            this.customerNumberUpdate(jwtDecoded?.CustomerNumber);
            this.coUserNumberUpdate(jwtDecoded?.CoUserNumber);
          });
        })
      ).subscribe();
    }
  }

  private userNameUpdate(userName: string | undefined) {
    let areStoredAndReceivedUserNamesEqual = this.userNameSubject.value === userName;
    if (areStoredAndReceivedUserNamesEqual) {
      return;
    }

    this.userNameSubject.next(userName);
  }

  private userInitialsUpdate(userInitials: string | undefined) {
    let areStoredAndReceivedUserInitialsEqual = this.userInitialsSubject.value === userInitials;
    if (areStoredAndReceivedUserInitialsEqual) {
      return;
    }

    this.userInitialsSubject.next(userInitials);
  }

  private userNumberUpdate(userNumber: string | undefined) {
    let areStoredAndReceivedUserNumbersEqual = this.userNumberSubject.value === userNumber;
    if (areStoredAndReceivedUserNumbersEqual) {
      return;
    }

    this.userNumberSubject.next(userNumber);
  }

  private customerNumberUpdate(customerNumber: string | undefined) {
    let areStoredAndReceivedCustomerNumbersEqual = this.customerNumberSubject.value === customerNumber;
    if (areStoredAndReceivedCustomerNumbersEqual) {
      return;
    }
    this.customerNumberSubject.next(customerNumber);
  }

  private coUserNumberUpdate(coUserNumber: string | undefined) {
    let areStoredAndReceivedCoUserNumbersEqual = this.coUserNumberSubject.value === coUserNumber;
    if (areStoredAndReceivedCoUserNumbersEqual) {
      return;
    }
    this.coUserNumberSubject.next(coUserNumber);
  }

  public accessJWTChangedGet(): Observable<string | undefined> {
    return this.accessJWTSubject.asObservable();
  }

  private authenticationStateUpdate(desiredAuthenticationState: boolean) {
    let currentAuthenticationState = this.isAuthenticated();
    let currentCookieTermsState = this.hasAcceptedCookieTerms();

    let hasLoginCookiesAccepted = ENVIRONMENT.auth.loginRequiresCookiesAcceptance ? currentCookieTermsState : true;

    if (desiredAuthenticationState && !hasLoginCookiesAccepted) {
      desiredAuthenticationState = false;
    }

    let hasAuthenticationStateChanged = currentAuthenticationState != desiredAuthenticationState;

    if (!hasAuthenticationStateChanged) {
      return;
    }

    this.authenticatedSource.next(desiredAuthenticationState);
  }

  public authenticatedGet(): Observable<boolean> {
    return this.authenticatedChanged;
  }

  public accessJWTSet(token: string): string | undefined {
    let areStoredAndReceivedTokensEqual = this.accessJWTSubject.value === token;
    if (areStoredAndReceivedTokensEqual) {
      return this.accessJWTSubject.value;
    }

    let isAccessJWTValid = this.accessJWTValidate(token);

    if (isAccessJWTValid) {
      let accessJWTStored: string | undefined = this.lStorage.accessJWTSet(token);
      let decodeToken = this.jwtHelper.decodeToken(token);
      this.userNumber = decodeToken.UserNumber;
      this.accessJWTSubjectSet(accessJWTStored);
      return accessJWTStored;
    } else {
      this.accessJWTClear();
      return undefined;
    }
  }

  public accessJWTClear() {
    this.lStorage.accessJWTClear();
    this.userNumber = undefined;
    this.accessJWTSubjectSet("");
    this.authenticationStateUpdate(false);
    this.contextService?.contextSetDefault();
  }

  public isAuthenticated(): boolean {
    return this.authenticatedSource.value;
  }

  private accessJWTSubjectSet(desiredAuthenticationState: string) {
    this.accessJWTSubject.next(desiredAuthenticationState);
  }

  private updateCookieTermsAcceptanceState(desiredCookieTermsState: boolean) {
    let currentCookieTermsState = this.hasAcceptedCookieTerms();
    let hasCookieTermsStateChanged = currentCookieTermsState != desiredCookieTermsState;

    if (!hasCookieTermsStateChanged) {
      return;
    }

    this.cookieTermsAcceptedStateSource.next(desiredCookieTermsState);
  }

  public cookieTermsAcceptanceStateGet(): Observable<boolean> {
    return this.cookieTermsAcceptedStateChanged;
  }

  public cookieTermsAcceptanceStateSet(value: string) {
    let cookieTermsStoredState: boolean = this.lStorage.cookieTermsAcceptanceStateSet(value);

    this.cookieTermsAcceptedStateSource.next(cookieTermsStoredState);
    this.updateCookieTermsAcceptanceState(cookieTermsStoredState);

    return cookieTermsStoredState;
  }

  public cookieTermsAcceptanceStateClear() {
    this.lStorage.cookieTermsAcceptanceStateClear();
    this.updateCookieTermsAcceptanceState(false);
  }

  public hasAcceptedCookieTerms(): boolean {
    return this.cookieTermsAcceptedStateSource.value;
  }

  public accessJWTValidateFromLocalStorage() {
    let accessJWT = this.lStorage.accessJWTGet();
    return this.accessJWTValidate(accessJWT);
  }

  public accessJWTValidate(token: string | undefined): boolean {
    if (token == undefined) {
      return false;
    }

    let decodeToken = this.jwtHelper.decodeToken(token);

    if (this.userNumber != undefined && this.userNumber != decodeToken.UserNumber) {
      window.location.reload();
      return true;
    }

    let isTokenExpired = this.jwtHelper.isTokenExpired(token);

    return !isTokenExpired;
  }

  public accessJWTValidityGet() {
    let accessJWT = this.lStorage.accessJWTGet();
    if (accessJWT == undefined) {
      return false;
    }

    return this.accessJWTValidate(accessJWT);
  }

  public userNumberChangedGet(): Observable<string | undefined> {
    return this.userNumberSubject.asObservable();
  }

  public coUserNumberChangedGet(): Observable<string | undefined> {
    return this.coUserNumberSubject.asObservable();
  }

  public customerNumberChangedGet(): Observable<string | undefined> {
    return this.customerNumberSubject.asObservable();
  }

  public userNameChangedGet(): Observable<string | undefined> {
    return this.userNameSubject.asObservable();
  }

  public userInitialsChangedGet(): Observable<string | undefined> {
    return this.userInitialsSubject.asObservable();
  }

  public isAccessJWTValid(accessJWT: string): boolean {
    let hasIsShopUserPermission = this.isPermissionIncludedInAccessJWT(EPermission.IsShopUser, accessJWT);

    let isAccessJWTValid = this.accessJWTValidate(accessJWT) && hasIsShopUserPermission;

    return isAccessJWTValid;
  }

  public isPermissionIncludedInAccessJWT(permission: EPermission, accessJWT: string): boolean {
    let jwtDecoded = this.jwtHelper.decodeToken(accessJWT);
    let permissionList = this.jwtPermissionToStringListGet(jwtDecoded?.Permission);
    let permissionListItem = permissionList.find(p => p === EPermission[permission]);
    return permissionListItem != undefined && permissionListItem.length > 0;
  }

  private jwtPermissionToStringListGet(jwtPermission: undefined | string | string[]): string[] {
    if (jwtPermission == undefined) {
      return [];
    }

    if (typeof jwtPermission === 'object') {
      return jwtPermission;
    }

    if (typeof jwtPermission === 'string') {
      return [jwtPermission];
    }

    return [];
  }

  public userPermissionChangedGet(): Observable<string[]> {
    return this.userPermissionSubject.asObservable();
  }

  public userPermissionGet(permissionName: string): string | undefined {
    let permissionList = this.userPermissionSubject.value;
    let permissionItem = permissionList.find(p => p === permissionName);
    return permissionItem;
  }

  public isShopUser(): boolean {
    return this.userPermissionGet(EPermission[EPermission.IsShopUser]) == undefined ? false : true;
  }

  public isMultiCustomerUser(): boolean {
    return this.userPermissionGet(EPermission[EPermission.IsMultiCustomerUser]) == undefined ? false : true;
  }

  public isUserAdmin(): boolean {
    return this.userPermissionGet(EPermission[EPermission.IsUserAdmin]) == undefined ? false : true;
  }

  public canSeePrices(): boolean {
    return this.userPermissionGet(EPermission[EPermission.CanSeePrices]) == undefined ? false : true;
  }

  public canPlaceOrders(): boolean {
    return this.userPermissionGet(EPermission[EPermission.CanPlaceOrders]) == undefined ? false : true;
  }

  public canPlaceOffers(): boolean {
    return this.userPermissionGet(EPermission[EPermission.CanPlaceOffers]) == undefined ? false : true;
  }

}

/**
 * Prepared provider with the token to implementation mapping.
 * Reference this at the desired provider list to enable the injection of this implementation by referencing the token(interface).
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export const AuthServiceProvider: Provider[] = [
  {
    provide: IAuthServiceToken,
    useClass: forwardRef(() => AuthService),
    deps: [
      ILocalStorageServiceToken
    ]
  },
  {
    provide: INITIALIZABLE_SERVICES,
    useExisting: IAuthServiceToken,
    multi: true,
  },
];
